import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle } from 'lucide-react';
import {
  REASON_LABELS,
  isSuperseded,
  transitionDirection,
} from '../../api/home_value_checks';
import type {
  AvailabilityLocation,
  HomeValueCheck,
  HomeValueReasonCode,
  HomeValueReviewStatus,
} from '../../api/home_value_checks';
import { Badge } from '../ui/badge';
import { Tooltip } from '../ui/tooltip';
import { NsIdLink } from './columns';
import { fmtDate, fmtDateTime, fmtVendor } from '../../utils/format';

/**
 * Shared column definitions for the Home Value Queue and Processed views
 * (BOT-411), defined once for the same reason the price-check columns are:
 * three screens showing one value three different ways is what the design
 * review objected to.
 */

// ── PDC chips ─────────────────────────────────────────────────────────────────

/** Location kinds that back a delivery promise; everything else is context. */
const PROMISE_KINDS = new Set(['pdc', 'unknown']);

/**
 * CAT's rule counts every source line, our dealer account's branch shelves and
 * the home store's `In Stock` included (client, 2026-09-14) — so there a dealer
 * row backs the promise and must not be greyed out like Paccar's `DLR`.
 */
const VENDOR_PROMISE_KINDS: Record<string, Set<string>> = {
  cat0: new Set(['pdc', 'unknown', 'dealer']),
  cat_: new Set(['pdc', 'unknown', 'dealer']),
};

function promiseKinds(vendorCode?: string): Set<string> {
  return VENDOR_PROMISE_KINDS[vendorCode ?? ''] ?? PROMISE_KINDS;
}

/** Same test the server's `parse_qty` applies: did the portal print a number? */
const NUMBER_RE = /^\s*-?\d[\d,]*(\.\d+)?\s*$/;

/**
 * What a chip shows for a cell. A blank cell and `<NOF>` are both stored as
 * `qty: 0` but are NOT a zero the portal printed — rendering them as `0` made a
 * page that never rendered look like a confirmed backorder. Only a parsed
 * number shows as a number; anything else shows the portal's own text.
 */
export function chipQuantity(loc: AvailabilityLocation): { text: string; parsed: boolean } {
  const raw = (loc.raw ?? '').trim();
  if (loc.qty == null) return { text: raw || '?', parsed: false };
  if (NUMBER_RE.test(raw)) return { text: String(loc.qty), parsed: true };
  return { text: raw || '—', parsed: false };
}

/**
 * "Dealer" means opposite things per vendor, and the tooltip must not lie about
 * it: Paccar's `DLR` is other dealers' shelves ("good to pull for competitor
 * data, cannot buy from"), Freightliner's rows are our own Fyda / ProFleet
 * branches and DECIDE the Home Value when no PDC has stock (client, 2026-09-15).
 */
const DEALER_TOOLTIP: Record<string, string> = {
  pac0: 'Dealer stock (DLR) — other dealers’ shelves, never counted',
  pacc: 'Dealer stock (DLR) — other dealers’ shelves, never counted',
  fre0: 'Our own dealer network (Fyda / ProFleet) — decides only when no PDC has stock',
  frei: 'Our own dealer network (Fyda / ProFleet) — decides only when no PDC has stock',
  int0: 'One dealer’s shelf (International) — reference only, never enters the rule',
  intl: 'One dealer’s shelf (International) — reference only, never enters the rule',
  cat0: 'Our dealer account’s branch shelf (Ohio CAT) — counts like any CAT source',
  cat_: 'Our dealer account’s branch shelf (Ohio CAT) — counts like any CAT source',
};

/**
 * One warehouse chip. Stock is emphasised, empties are muted, and dealer /
 * supplier rows are greyed and labelled — they never back a PDC promise on
 * their own (client answer K2, and the 2870020CUM case where DLR 7 with every
 * PDC at 0 was a genuine backorder).
 */
function LocationChip({ loc, vendorCode }: { loc: AvailabilityLocation; vendorCode?: string }) {
  const promise = promiseKinds(vendorCode).has(loc.kind);
  const { text, parsed } = chipQuantity(loc);
  const inStock = parsed && (loc.qty ?? 0) > 0;
  const base =
    loc.kind === 'dealer'
      ? (DEALER_TOOLTIP[vendorCode ?? ''] ?? 'Dealer stock — never a PDC promise')
    : loc.kind === 'supplier' ? 'Supplier stock — never counted'
    : loc.kind === 'excluded'
      ? loc.flags?.generic_cat
        ? 'Bare “CAT” line — the factory, not a warehouse; never counted'
        : 'Contact Dealer — the shortfall against the probed 100, not stock; never counted'
    : loc.kind === 'unknown' ? 'Unrecognised location code — counted, please classify'
    : loc.flags?.star ? 'PDC (portal marked this code with *)'
    : loc.flags?.primary_dealer ? 'Vendor PDC'
    : 'Vendor PDC';
  const home = loc.kind === 'dealer' && loc.flags?.primary_dealer ? ' · home branch' : '';
  const suffix = parsed
    ? ''
    : loc.flags?.no_stock_marker
      ? ' · not on file (the portal answered)'
      : loc.flags?.status
        ? ' · a status, not a quantity (International reports no PDC numbers)'
        : ' · portal printed no number';
  const title = `${base}${home}${suffix}`;

  return (
    <Tooltip label={title}>
      <span
        className={
          !promise
            ? 'inline-flex items-baseline gap-1 rounded-sm bg-zinc-50 px-1 text-[11px] text-zinc-400'
            : inStock
              ? 'inline-flex items-baseline gap-1 rounded-sm bg-green-50 px-1 text-[11px] font-medium text-green-800'
              : 'inline-flex items-baseline gap-1 rounded-sm px-1 text-[11px] text-zinc-400'
        }
      >
        <span className="font-mono">{loc.code}</span>
        <span className={parsed ? 'tabular-nums' : 'font-mono text-[10px] italic'}>{text}</span>
        {loc.kind === 'unknown' && (
          <AlertTriangle className="h-2.5 w-2.5" aria-label="Unrecognised location code" />
        )}
      </span>
    </Tooltip>
  );
}

/**
 * PDCs first (promise-bearing), then dealer/supplier as greyed context.
 *
 * `hideSupplier` drops the SUP chips from table cells (client, 2026-09-15:
 * "SUP quantities are sort of agnostic and can be hidden") — the expanded row
 * keeps them, so the audit still sees what the portal printed.
 */
export function LocationChips({
  locations,
  vendorCode,
  hideSupplier = false,
}: {
  locations: AvailabilityLocation[];
  vendorCode?: string;
  hideSupplier?: boolean;
}) {
  if (!locations?.length) {
    return <span className="text-xs text-zinc-300">no stock table</span>;
  }
  const kinds = promiseKinds(vendorCode);
  const promise = locations.filter(l => kinds.has(l.kind));
  const other = locations.filter(
    l => !kinds.has(l.kind) && !(hideSupplier && l.kind === 'supplier'),
  );
  return (
    <div className="flex max-w-[420px] flex-wrap items-center gap-x-1 gap-y-0.5">
      {promise.map((l, i) => <LocationChip key={`${l.code}-${i}`} loc={l} vendorCode={vendorCode} />)}
      {other.length > 0 && promise.length > 0 && (
        <span className="px-0.5 text-zinc-200">|</span>
      )}
      {other.map((l, i) => <LocationChip key={`o-${l.code}-${i}`} loc={l} vendorCode={vendorCode} />)}
    </div>
  );
}

// ── transition ────────────────────────────────────────────────────────────────

const DIRECTION_VARIANT = {
  faster: 'success',
  slower: 'warning',
  same: 'neutral',
  unknown: 'info',
} as const;

/**
 * current → proposed, tinted by direction.
 *
 * The two directions carry different risks and the badge says which: a faster
 * promise risks a cancellation if we are wrong (the largest RMA category), a
 * slower one risks a lost sale. Amber for slower is not "bad" — it is "this one
 * takes something away from the storefront".
 */
export function TransitionBadge({ check }: { check: HomeValueCheck }) {
  const direction = transitionDirection(check);
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <span className="text-[11.5px] text-muted-foreground">
        {check.current_home_value?.trim() || '(none)'}
      </span>
      <span className="text-zinc-300">→</span>
      <Badge variant={DIRECTION_VARIANT[direction]}>{check.proposed_home_value}</Badge>
    </div>
  );
}

// ── columns ───────────────────────────────────────────────────────────────────

// The identity columns are generic factories: the No data page lists live
// availability rows, not proposals, and must render the same cells.

export function skuColumnFor<T extends { sku: string | null; item_name: string | null }>(): ColumnDef<T> {
  return {
    id: 'sku',
    size: 230,
    enableHiding: false,
    meta: { label: 'SKU' },
    header: 'SKU',
    cell: ({ row }) => (
      <>
        <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">
          {row.original.sku ?? '—'}
        </div>
        {row.original.item_name && (
          <div className="max-w-[240px] truncate text-xs text-muted-foreground"
               title={row.original.item_name}>
            {row.original.item_name}
          </div>
        )}
      </>
    ),
  };
}

export function vendorColumnFor<T extends { vendor_name: string }>(): ColumnDef<T> {
  return {
    id: 'vendor',
    meta: { label: 'Vendor' },
    header: 'Vendor',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-zinc-700">
        {fmtVendor(row.original.vendor_name)}
      </span>
    ),
  };
}

export function manufacturerColumnFor<T extends { manufacturer: string | null }>(): ColumnDef<T> {
  return {
    id: 'manufacturer',
    meta: { label: 'Manufacturer' },
    header: 'Manufacturer',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-zinc-700">
        {row.original.manufacturer ?? '—'}
      </span>
    ),
  };
}

export function nsIdColumnFor<T extends { ns_item_id: number | null }>(): ColumnDef<T> {
  return {
    id: 'ns',
    meta: { label: 'NS ID' },
    header: 'NS ID',
    cell: ({ row }) => <NsIdLink id={row.original.ns_item_id} />,
  };
}

export const skuColumn: ColumnDef<HomeValueCheck> = skuColumnFor<HomeValueCheck>();
export const vendorColumn: ColumnDef<HomeValueCheck> = vendorColumnFor<HomeValueCheck>();
export const manufacturerColumn: ColumnDef<HomeValueCheck> = manufacturerColumnFor<HomeValueCheck>();
export const nsIdColumn: ColumnDef<HomeValueCheck> = nsIdColumnFor<HomeValueCheck>();

export const transitionColumn: ColumnDef<HomeValueCheck> = {
  id: 'transition',
  size: 300,
  meta: { label: 'Home Value' },
  header: 'Home Value',
  cell: ({ row }) => <TransitionBadge check={row.original} />,
};

export const reasonColumn: ColumnDef<HomeValueCheck> = {
  id: 'reason',
  meta: { label: 'Reason' },
  header: 'Reason',
  cell: ({ row }) => {
    const code = row.original.reason_code;
    if (!code) return <span className="text-xs text-zinc-300">—</span>;
    return (
      <Badge variant="neutral">
        {REASON_LABELS[code as HomeValueReasonCode] ?? code}
      </Badge>
    );
  },
};

export const locationsColumn: ColumnDef<HomeValueCheck> = {
  id: 'locations',
  size: 440,
  meta: { label: 'Vendor stock' },
  header: 'Vendor stock',
  cell: ({ row }) => (
    <LocationChips
      locations={row.original.locations}
      vendorCode={row.original.vendor_code}
      hideSupplier
    />
  ),
};

/**
 * The proposal snapshot's PDC total (client, 2026-09-15: "We would need a
 * 'total' column for total in vendor warehouses"). `—` and `0` are different
 * facts — the portal gave no readable number vs the portal confirmed zero — and
 * that distinction is the whole point of the lane, so they never collapse.
 */
export const vendorTotalColumn: ColumnDef<HomeValueCheck> = {
  id: 'vendorTotal',
  meta: { label: 'Vendor total', align: 'right' },
  header: 'Vendor total',
  cell: ({ row }) => {
    const total = row.original.pdc_total;
    if (total == null) {
      return (
        <Tooltip label="The portal gave no readable PDC number (not the same as zero)">
          <span aria-label="Vendor total" className="text-xs text-zinc-300">—</span>
        </Tooltip>
      );
    }
    return (
      <Tooltip label="Units across the vendor's PDCs — dealer and supplier rows excluded">
        <span aria-label="Vendor total" className="whitespace-nowrap tabular-nums text-zinc-700">{total}</span>
      </Tooltip>
    );
  },
};

/**
 * Dealer stock, split out of the chips (client, 2026-09-15: "split the Dealer
 * out in its own column"). The tooltip is vendor-dependent because "dealer"
 * means opposite things per vendor, and it lists the branches behind the number.
 */
export const dealerColumn: ColumnDef<HomeValueCheck> = {
  id: 'dealer',
  meta: { label: 'Dealer', align: 'right' },
  header: 'Dealer',
  cell: ({ row }) => {
    const { dealer_total, locations, vendor_code } = row.original;
    const meaning = DEALER_TOOLTIP[vendor_code] ?? 'Dealer stock — never a PDC promise';
    const breakdown = (locations ?? [])
      .filter(l => l.kind === 'dealer')
      .map(l => `${l.code} ${chipQuantity(l).text}`)
      .join(' · ');
    const label = breakdown ? `${meaning} — ${breakdown}` : meaning;
    if (dealer_total == null) {
      return (
        <Tooltip label={label}>
          <span aria-label="Dealer stock" className="text-xs text-zinc-300">—</span>
        </Tooltip>
      );
    }
    return (
      <Tooltip label={label}>
        <span aria-label="Dealer stock" className="whitespace-nowrap tabular-nums text-zinc-700">{dealer_total}</span>
      </Tooltip>
    );
  },
};

/** How old the observation behind the proposal is — the trust signal. */
export const observedColumn: ColumnDef<HomeValueCheck> = {
  id: 'observed',
  meta: { label: 'Observed' },
  header: 'Observed',
  cell: ({ row }) => {
    const { availability_observed_at, age_days } = row.original;
    const superseded = isSuperseded(row.original);
    return (
      <div className="whitespace-nowrap">
        <div className="text-muted-foreground">{fmtDate(availability_observed_at)}</div>
        <div className="flex items-center gap-1 text-[11.5px] text-zinc-400">
          {age_days != null ? `${Math.round(age_days)}d old` : '—'}
          {superseded && (
            <Tooltip label="A newer scrape has landed since this proposal was computed">
              <AlertTriangle
                className="h-3 w-3 text-amber-600"
                aria-label="Superseded by a newer scrape"
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  },
};

/**
 * NetSuite stock, display only.
 *
 * It never enters the rule: Home Value answers what happens once OUR shelf is
 * empty (the Magento PDP only shows it when quantity is 0), so a part with stock
 * on hand is not evidence about the vendor's lead time. It is here because an
 * operator wants to know how urgent a wrong promise is.
 */
export function nsStockColumnFor<
  T extends { ns_on_hand: number | null; ns_available: number | null },
>(): ColumnDef<T> {
  return {
    id: 'nsStock',
    meta: { label: 'NS stock', align: 'right' },
    header: 'NS stock',
    cell: ({ row }) => {
      const { ns_on_hand, ns_available } = row.original;
      if (ns_on_hand == null && ns_available == null) {
        return <span className="text-xs text-zinc-300">—</span>;
      }
      return (
        <Tooltip label="On hand / available to sell in NetSuite — context only, not part of the rule">
          <span className="whitespace-nowrap tabular-nums text-zinc-600">
            {ns_on_hand ?? '—'} / {ns_available ?? '—'}
          </span>
        </Tooltip>
      );
    },
  };
}

export const nsStockColumn: ColumnDef<HomeValueCheck> = nsStockColumnFor<HomeValueCheck>();

/** What the push will do once the sync job picks the row up (Queue tab) — the
 *  Home Value twin of the price lane's `QueueActionBadge`. */
export function HomeValueActionBadge({ reviewStatus }: { reviewStatus: HomeValueReviewStatus }) {
  if (reviewStatus === 'auto_approved') {
    return <Badge variant="success" dot="bg-green-500">Auto push</Badge>;
  }
  return <Badge variant="info" dot="bg-sky-500">Home Value push</Badge>;
}

/**
 * When the row entered the push queue: the reviewer's click, or — for an
 * auto-approved row that never saw a reviewer — its insert. A failed push is
 * flagged here so the queue explains why a row is still waiting.
 */
export const queuedAtColumn: ColumnDef<HomeValueCheck> = {
  id: 'queuedAt',
  meta: { label: 'Queued at' },
  header: 'Queued at',
  cell: ({ row }) => {
    const c = row.original;
    return (
      <div className="whitespace-nowrap">
        <span className="text-muted-foreground">{fmtDateTime(c.reviewed_at ?? c.created_at)}</span>
        {c.ns_push_error && (
          <Tooltip label={c.ns_push_error}>
            <div className="flex items-center gap-1 text-[11.5px] text-red-600">
              <AlertTriangle className="h-3 w-3" /> push failed — retrying
            </div>
          </Tooltip>
        )}
      </div>
    );
  },
};

export const reviewerColumn: ColumnDef<HomeValueCheck> = {
  id: 'reviewer',
  meta: { label: 'Reviewed by' },
  header: 'Reviewed by',
  cell: ({ row }) => (
    <span className="whitespace-nowrap text-muted-foreground">
      {row.original.reviewer_email ?? '—'}
    </span>
  ),
};

export const syncedColumn: ColumnDef<HomeValueCheck> = {
  id: 'synced',
  meta: { label: 'Synced at' },
  header: 'Synced at',
  cell: ({ row }) => (
    <div className="whitespace-nowrap">
      <span className="text-muted-foreground">{fmtDateTime(row.original.ns_pushed_at)}</span>
      {row.original.ns_push_error && (
        <Tooltip label={row.original.ns_push_error}>
          <div className="flex items-center gap-1 text-[11.5px] text-red-600">
            <AlertTriangle className="h-3 w-3" /> push failed
          </div>
        </Tooltip>
      )}
    </div>
  ),
};
